'use client';
import { AnimatePresence, motion } from 'framer-motion';
import type { FC } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { PaidTrialInfo } from '@/app/types';
import { cn } from '@/app/utils';
import { DiscountBanner, useHasDiscountBanner } from './DiscountBanner';
import { PaidTrialBanner } from './PaidTrialBanner';

const SLIDE_DURATION_MS = 4000;

/**
 * 订阅页横幅区域：
 * - 只有折扣活动 → 只展示 DiscountBanner
 * - 只有试用信息 → 只展示 PaidTrialBanner
 * - 两者都有     → 轮播（每 4 秒切换，右下角显示进度指示器）
 * - 都没有       → 不渲染
 */
export const SubscriptionBanner: FC<{
  paidTrialInfo?: PaidTrialInfo | null;
  className?: string;
}> = ({ paidTrialInfo, className }) => {
  const hasDiscount = useHasDiscountBanner();
  const hasTrial = Boolean(paidTrialInfo);
  const carousel = hasDiscount && hasTrial;

  const [activeIdx, setActiveIdx] = useState(0);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 轮播时的 slides 数组
  const slides = useMemo(() => {
    if (!carousel || !paidTrialInfo) return [];
    return [
      { id: 'discount', node: <DiscountBanner className="mt-0" /> },
      { id: 'trial', node: <PaidTrialBanner paidTrial={paidTrialInfo} /> },
    ] as const;
  }, [carousel, paidTrialInfo]);

  // 仅轮播时启动定时器
  useEffect(() => {
    if (!carousel || slides.length === 0) return;
    timerRef.current = setInterval(() => {
      setActiveIdx((i) => (i + 1) % slides.length);
    }, SLIDE_DURATION_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [carousel, slides.length]);

  if (!hasDiscount && !hasTrial) return null;

  const baseClass = cn('relative mt-3 sm:mx-4', className);

  // 单独展示
  if (!carousel || !paidTrialInfo) {
    return (
      <div className={baseClass}>
        {hasTrial && paidTrialInfo ? <PaidTrialBanner paidTrial={paidTrialInfo} /> : <DiscountBanner />}
      </div>
    );
  }

  return (
    <div className={cn(baseClass, 'overflow-hidden')}>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={slides[activeIdx].id}
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -40 }}
          transition={{ duration: 0.35, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {slides[activeIdx].node}
        </motion.div>
      </AnimatePresence>

      {/* 进度指示器 — 右下角，匹配 Figma 设计 */}
      <div className="absolute right-5 bottom-3 flex items-center gap-[5px]">
        {slides.map((slide, i) => (
          <button
            key={slide.id}
            type="button"
            aria-label={`Go to slide ${i + 1}`}
            onClick={() => {
              setActiveIdx(i);
              if (timerRef.current) clearInterval(timerRef.current);
              timerRef.current = setInterval(() => {
                setActiveIdx((idx) => (idx + 1) % slides.length);
              }, SLIDE_DURATION_MS);
            }}
            className={cn(
              'h-[5px] rounded-full bg-white transition-all duration-300',
              i === activeIdx ? 'w-[40px] opacity-100' : 'w-[16px] opacity-30',
            )}
          />
        ))}
      </div>
    </div>
  );
};
